import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";

/**
 * Export Profit & Loss data to Excel
 * @param {Object} params
 * @param {Array} params.profitLossData - profit & loss array
 * @param {string} params.fromDate - report start date
 * @param {string} params.toDate - report end date
 * @param {string} params.societyName - society / company name
 */
export const exportBalSheetExcel = ({ profitLossData, fromDate, toDate, societyName }) => {
  const wb = XLSX.utils.book_new();
  const wsData = [];

  const formatDate = (isoDate) => {
    if (!isoDate) return "";
    const [year, month, day] = isoDate.split("-");
    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (amount) => {
    if (amount != null && amount !== "") {
      const numberFormat = new Intl.NumberFormat("en-IN", {
        style: "decimal",
        minimumFractionDigits: 2,
      });
      return numberFormat.format(amount);
    } else {
      return ""; // keep blank if null/undefined
    }
  };

  // --- Report Header ---
  wsData.push([societyName]);
  wsData.push([]); // spacing
  wsData.push([`Profit & Loss Report For the Period: ${formatDate(fromDate)} To ${formatDate(toDate)}`]);
  wsData.push([]); // spacing

  // --- Table Header ---
  wsData.push(["Particulars", "Amount"]);

  // --- Table Body ---
  if (profitLossData && profitLossData.length > 0) {
    profitLossData.forEach((pl) => {
      const row = [pl.particulars, formatCurrency(pl.amount)];
      wsData.push(row);
    });
  } else {
    wsData.push(["No Record found.", ""]);
  }

  // --- Create Worksheet ---
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // --- Styling ---
  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = ws[cellRef];
      if (!cell) continue;

      const value = cell.v ? cell.v.toString().trim() : "";

      // --- Header Row (Particulars / Amount) ---
      if (R === 4) {
        cell.s = {
          font: { bold: true },
          alignment: { horizontal: "center" },
          fill: { fgColor: { rgb: "E6E6E6" } }, // light gray
        };
        continue;
      }

      // --- Title Rows (society name + heading) ---
      if (R === 0 || R === 2) {
        cell.s = {
          font: { bold: true, sz: 13 },
          alignment: { horizontal: "center" },
        };
        if (!ws["!merges"]) ws["!merges"] = [];
        ws["!merges"].push({ s: { r: R, c: 0 }, e: { r: R, c: 1 } }); // merge across 2 cols
        continue;
      }

      // --- Highlight totals ---
      if (["TOTAL INCOME", "TOTAL EXPENSES"].includes(value)) {
        cell.s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "F0F0F0" } },
        };
      }

      // --- Blue group headers (inc_exp = 1 or 11) ---
      const pl = profitLossData[R - 5]; // offset rows before data
      if (pl && (pl.inc_exp === 1 || pl.inc_exp === 11)) {
        cell.s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "CCE5FF" } }, // light blue
        };
      }

      // --- NET PROFIT / NET LOSS rows ---
      if (value === "NET PROFIT") {
        cell.s = {
          font: { bold: true, color: { rgb: "008000" } }, // green
        };
      } else if (value === "NET LOSS") {
        cell.s = {
          font: { bold: true, color: { rgb: "FF0000" } }, // red
        };
      }

      // --- Right align the Amount column ---
      if (C === 1) {
        cell.s = {
          ...(cell.s || {}),
          alignment: { horizontal: "right" },
        };
      }
    }
  }

  // --- Column widths ---
  ws["!cols"] = [
    { wch: 50 }, // Particulars
    { wch: 20 }, // Amount
  ];

  // --- Add Worksheet to Workbook ---
  XLSX.utils.book_append_sheet(wb, ws, "Profit & Loss");

  // --- Save Excel File ---
  const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(
    new Blob([wbout], { type: "application/octet-stream" }),
    `ProfitLoss_Report_${formatDate(fromDate)}_To_${formatDate(toDate)}.xlsx`
  );
};
